import React, { useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { getStatusConfig } from '../utils/statusConfig';
import { toLegacyStatusConfig } from '../utils/statusUtils';
import { NODE_WIDTH } from '../utils/constants';
import { renderIcon } from '../utils/renderIcon';
import './ConfirmModal.css';
import './InProgressLockWarningModal.css';

// Crown icon for primary tag - matches TriggerButton.js
const CrownIcon = () => (
  <svg 
    className="mini-mission-card__tag-crown" 
    viewBox="0 0 24 24" 
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm14 3c0 .6-.4 1-1 1H6c-.6 0-1-.4-1-1v-1h14v1z"/>
  </svg>
);

/**
 * Ensure tag color is readable (from TagsDropdown/utils/colorUtils.js)
 */
const ensureReadableColor = (color) => {
  if (!color) return '#6366f1';
  return color;
};

/**
 * StatusBadge - Renders a status badge matching the Flow view status dropdown trigger
 */
const StatusBadge = ({ statusId, statuses }) => {
  const statusConfig = getStatusConfig(statusId, statuses);
  const legacyConfig = toLegacyStatusConfig(statusConfig);
  
  return (
    <span 
      className="in-progress-lock-modal__status-badge"
      style={{
        '--status-color': statusConfig.color,
        '--status-bg': legacyConfig.bg,
      }}
    >
      {statusConfig.name}
    </span>
  );
};

/**
 * Mini Mission Card - Renders a simplified task card matching Flow view styling
 * Shows: icon, name, category bar (if tag), primary tag
 * Uses exact same dimensions as Flow view task nodes (NODE_WIDTH)
 */
const MiniMissionCard = ({ 
  task, 
  allTags = [],
  variant = 'default', // 'highlighted' for affected task, 'blocking' for dependencies
  showLockBadge = false,
}) => {
  // Get primary tag name from task
  const primaryTagName = task?.category || task?.tags?.[0];
  
  // Look up the full tag object from allTags
  const primaryTag = useMemo(() => {
    if (!primaryTagName) return null;
    const found = allTags.find(t => t.name === primaryTagName);
    return found || { name: primaryTagName, color: '#6366f1' };
  }, [primaryTagName, allTags]);
  
  const hasCategory = !!primaryTag;
  
  // Get tag colors
  const tagColor = primaryTag?.color || '#6366f1';
  
  // Build CSS variables for category styling
  const style = {
    width: NODE_WIDTH,
    ...(hasCategory ? {
      '--category-color': tagColor,
      '--category-bg': `${tagColor}15`, // 8% opacity for gradient
    } : {}),
  };
  
  // Build class names
  const classNames = [
    'mini-mission-card',
    hasCategory && 'mini-mission-card--has-category',
    variant === 'highlighted' && 'mini-mission-card--highlighted',
    variant === 'blocking' && 'mini-mission-card--blocking',
  ].filter(Boolean).join(' ');
  
  // Check if task has an icon
  const hasIcon = !!task?.icon;
  
  return (
    <div className={classNames} style={style}>
      {/* Header: Icon + Name + Lock indicator */}
      <div className={`mini-mission-card__header ${!hasIcon ? 'mini-mission-card__header--no-icon' : ''}`}>
        {/* Icon - only render if task has an icon */}
        {hasIcon && (
          <div className="mini-mission-card__icon">
            {renderIcon(task.icon, { 
              size: '18px',
              nodeColor: primaryTag?.color,
            })}
          </div>
        )}
        
        {/* Name */}
        <div className={`mini-mission-card__name ${!task?.name ? 'mini-mission-card__name--placeholder' : ''}`}>
          {task?.name || 'Unnamed mission'}
          {task?.nodeType === 'decision' && (
            <span className="mini-mission-card__decision-badge">
              <span className="mini-mission-card__decision-icon">◇</span>
              DECISION
            </span>
          )}
        </div>
        
        {/* Lock indicator - matches mission-card__lock-indicator */}
        {showLockBadge && (
          <div className="mini-mission-card__lock-indicator" title="This mission is now locked">
            <svg 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
              className="mini-mission-card__lock-icon"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
        )}
      </div>
      
      {/* Primary tag - matches task-node__tag-pill styling exactly */}
      {primaryTag && (
        <div className="mini-mission-card__tags-row">
          <span 
            className="mini-mission-card__tag-pill"
            style={{ '--pill-color': ensureReadableColor(primaryTag.color) }}
          >
            <CrownIcon />
            <span className="mini-mission-card__tag-name">{primaryTag.name}</span>
          </span>
        </div>
      )}
    </div>
  );
};

/**
 * InProgressLockWarningModal
 * 
 * Shows when a user adds a condition to an "in progress" mission that would lock it.
 * Displays the affected mission and its new blocking dependencies side by side.
 */
const InProgressLockWarningModal = ({
  isOpen,
  affectedTask,
  blockingTasks = [],
  statuses = [],
  allTags = [],
  onConfirm, // User wants to switch back to "To Do"
  onCancel,  // User wants to keep the current status
}) => {
  const confirmBtnRef = useRef(null);
  
  // Focus confirm button when modal opens
  useEffect(() => {
    if (isOpen && confirmBtnRef.current) {
      confirmBtnRef.current.focus();
    }
  }, [isOpen]);
  
  // Determine how many blocking tasks to show (max 3 visible, rest as "+X more")
  const MAX_VISIBLE_BLOCKING = 3;
  const visibleBlockingTasks = blockingTasks.slice(0, MAX_VISIBLE_BLOCKING);
  const remainingCount = blockingTasks.length - MAX_VISIBLE_BLOCKING;
  
  if (!isOpen) return null;
  
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onCancel?.();
    }
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onCancel?.();
    } else if (e.key === 'Enter') {
      onConfirm?.();
    }
  };
  
  // Format blocking task names for the message
  const formatBlockingNames = () => {
    if (blockingTasks.length === 0) return 'another mission';
    if (blockingTasks.length === 1) {
      return blockingTasks[0]?.name || 'Unnamed mission';
    }
    if (blockingTasks.length === 2) {
      return `${blockingTasks[0]?.name || 'Unnamed'} and ${blockingTasks[1]?.name || 'Unnamed'}`;
    }
    const remaining = blockingTasks.length - 1;
    return `${blockingTasks[0]?.name || 'Unnamed'} and ${remaining} other${remaining > 1 ? 's' : ''}`;
  };
  
  const modal = (
    <div
      className="confirm-modal__backdrop in-progress-lock-modal"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className="confirm-modal__container" data-variant="warning">
        {/* Header */}
        <div className="confirm-modal__header">
          <div className="confirm-modal__icon-wrapper">
            <div className="confirm-modal__orbit" />
            <div className="confirm-modal__particle" />
            <div className="confirm-modal__particle confirm-modal__particle--secondary" />
            <div className="confirm-modal__icon confirm-modal__icon--warning">
              <span>🔒</span>
            </div>
          </div>
          <h3 className="confirm-modal__title">This mission is now locked</h3>
        </div>
        
        {/* Body */}
        <div className="confirm-modal__body">
          {/* Cleaner message format with mission name on its own line */}
          <div className="in-progress-lock-modal__message">
            <p className="in-progress-lock-modal__message-line">
              <span className="in-progress-lock-modal__mission-name">"{affectedTask?.name || 'This mission'}"</span>
            </p>
            <p className="in-progress-lock-modal__message-line">
              was marked as <StatusBadge statusId={affectedTask?.status} statuses={statuses} /> — which means you were working on it.
            </p>
            <p className="in-progress-lock-modal__message-line">
              But with this new condition, it now requires:
            </p>
            <p className="in-progress-lock-modal__message-line">
              <span className="in-progress-lock-modal__mission-name">"{formatBlockingNames()}"</span>
            </p>
            <p className="in-progress-lock-modal__message-line">
              to be completed first.
            </p>
          </div>
          
          {/* Horizontal card layout */}
          <div className="in-progress-lock-modal__cards-row">
            {/* Left: Affected mission */}
            <div className="in-progress-lock-modal__affected-side">
              <div className="in-progress-lock-modal__section-label">This mission</div>
              <MiniMissionCard 
                task={affectedTask} 
                allTags={allTags}
                variant="highlighted"
                showLockBadge
              />
            </div>
            
            {/* Arrow */}
            <div className="in-progress-lock-modal__arrow">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
              <span className="in-progress-lock-modal__arrow-label">now requires</span>
            </div>
            
            {/* Right: Blocking missions */}
            <div className="in-progress-lock-modal__blocking-side">
              <div className="in-progress-lock-modal__section-label">
                {blockingTasks.length === 1 ? 'This mission' : 'These missions'} to be completed first
              </div>
              <div className="in-progress-lock-modal__blocking-grid">
                {visibleBlockingTasks.map((task, index) => (
                  <MiniMissionCard 
                    key={task?.id || index}
                    task={task}
                    allTags={allTags}
                    variant="blocking"
                  />
                ))}
                {remainingCount > 0 && (
                  <div className="in-progress-lock-modal__more-indicator">
                    +{remainingCount} more mission{remainingCount > 1 ? 's' : ''}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <p className="in-progress-lock-modal__question">
            Would you like to switch it back to "To Do"?
          </p>
        </div>
        
        {/* Footer */}
        <div className="confirm-modal__footer">
          <button
            className="confirm-modal__btn confirm-modal__btn--cancel"
            onClick={onCancel}
          >
            No, I know what I'm doing
          </button>
          <button
            ref={confirmBtnRef}
            className="confirm-modal__btn confirm-modal__btn--warning"
            onClick={onConfirm}
          >
            Yes, switch to "To Do"
          </button>
        </div>
      </div>
    </div>
  );
  
  return createPortal(modal, document.body);
};

export default InProgressLockWarningModal;
